import React, { useState } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { financeiroApi } from '../../../services/api';
import type { DREData } from '../../../types';

const DRE: React.FC = () => {
  const [data, setData] = useState<DREData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const handleConsultar = async () => {
    if (!dataInicio || !dataFim) {
      setError('Por favor, informe as datas de início e fim.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await financeiroApi.getDRE(dataInicio, dataFim);
      setData(response);
    } catch (err) {
      setError('Erro ao carregar DRE');
      console.error('Error loading DRE:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('pt-BR').format(new Date(dateString));
  };

  // Preencher datas padrão (mês atual)
  React.useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setDataInicio(primeiroDia.toISOString().split('T')[0]);
    setDataFim(ultimoDia.toISOString().split('T')[0]);
  }, []);

  return (
    <div className="space-y-4">
      <div className="mb-8">
        <h1 className="text-base font-bold text-gray-900">DRE - Demonstrativo de Resultado</h1>
        <p className="mt-0.5 text-xs text-gray-700">
          Demonstrativo de receitas e despesas por categoria
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <ExclamationCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="data_inicio" className="block text-sm font-medium text-gray-700 mb-1">
              Data Início
            </label>
            <input
              type="date"
              id="data_inicio"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="data_fim" className="block text-sm font-medium text-gray-700 mb-1">
              Data Fim
            </label>
            <input
              type="date"
              id="data_fim"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleConsultar}
              disabled={loading}
              className="w-full px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Carregando...' : 'Consultar'}
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      )}

      {data && !loading && (
        <>
          {/* Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-500">Total Receitas</h3>
              <p className="mt-2 text-2xl font-semibold text-green-600">
                {formatCurrency(data.receitas.total)}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Período: {formatDate(dataInicio)} a {formatDate(dataFim)}
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-500">Total Despesas</h3>
              <p className="mt-2 text-2xl font-semibold text-red-600">
                {formatCurrency(data.despesas.total)}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Período: {formatDate(dataInicio)} a {formatDate(dataFim)}
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-500">Resultado</h3>
              <p
                className={`mt-2 text-2xl font-semibold ${
                  data.resultado >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(data.resultado)}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {data.resultado >= 0 ? 'Lucro' : 'Prejuízo'}
              </p>
            </div>
          </div>

          {/* Receitas por Categoria */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-6">
            <div className="px-6 py-4 bg-green-50 border-b border-gray-200">
              <h2 className="text-sm font-bold text-green-900">Receitas por Categoria</h2>
            </div>
            <div className="overflow-x-auto">
              {data.receitas.categorias && data.receitas.categorias.length > 0 ? (
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Categoria
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valor
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % do Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {data.receitas.categorias.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            {item.categoria__nome}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-green-600">
                            {formatCurrency(item.total)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-green-50 font-semibold">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-900">TOTAL RECEITAS</td>
                      <td className="px-4 py-3 text-right text-sm text-green-600">
                        {formatCurrency(data.receitas.total)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">100%</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="text-center py-12">
                  <p className="text-xs text-gray-500">Nenhuma receita no período.</p>
                </div>
              )}
            </div>
          </div>

          {/* Despesas por Categoria */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-6">
            <div className="px-6 py-4 bg-red-50 border-b border-gray-200">
              <h2 className="text-sm font-bold text-red-900">Despesas por Categoria</h2>
            </div>
            <div className="overflow-x-auto">
              {data.despesas.categorias && data.despesas.categorias.length > 0 ? (
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Categoria
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valor
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % do Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {data.despesas.categorias.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            {item.categoria__nome}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-red-600">
                            {formatCurrency(item.total)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-red-50 font-semibold">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-900">TOTAL DESPESAS</td>
                      <td className="px-4 py-3 text-right text-sm text-red-600">
                        {formatCurrency(data.despesas.total)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">100%</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="text-center py-12">
                  <p className="text-xs text-gray-500">Nenhuma despesa no período.</p>
                </div>
              )}
            </div>
          </div>

          {/* Resultado Final */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
            <div
              className={`px-6 py-4 ${
                data.resultado >= 0 ? 'bg-green-50' : 'bg-red-50'
              } border-b border-gray-200`}
            >
              <h2
                className={`text-lg font-semibold ${
                  data.resultado >= 0 ? 'text-green-900' : 'text-red-900'
                }`}
              >
                Resultado do Período
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Total de Receitas:</span>
                  <span className="text-lg font-semibold text-green-600">
                    {formatCurrency(data.receitas.total)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Total de Despesas:</span>
                  <span className="text-lg font-semibold text-red-600">
                    -{formatCurrency(data.despesas.total)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 bg-gray-50 px-4 rounded-md">
                  <span className="text-base font-bold text-gray-900">
                    {data.resultado >= 0 ? 'LUCRO' : 'PREJUÍZO'}:
                  </span>
                  <span
                    className={`text-2xl font-bold ${
                      data.resultado >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {formatCurrency(Math.abs(data.resultado))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {!data && !loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <ExclamationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Selecione o período</h3>
          <p className="mt-1 text-xs text-gray-500">
            Informe as datas e clique em "Consultar" para visualizar o DRE.
          </p>
        </div>
      )}
    </div>
  );
};

export default DRE;
